import React, { useEffect, useRef, useState } from 'react';
import { useDismissOnOutsideClick } from '../hooks/useDismissOnOutsideClick';

interface Props<T> {
  id?: string;
  selected: T[];
  onChange: (items: T[]) => void;
  getKey: (item: T) => string;
  getLabel: (item: T) => string;
  getSublabel?: (item: T) => string;
  /** Per-item icon color. Chips use this color; dropdown items use default. */
  getColor?: (item: T) => string;
  icon?: string;
  placeholder?: string;
  // Static mode: pass items and the component filters internally.
  items?: T[];
  // Async mode: pass pre-filtered suggestions and receive query changes.
  suggestions?: T[];
  onQueryChange?: (query: string) => void;
  // When provided, pressing Enter on a non-empty query adds a free-text entry.
  onFreeEntry?: (value: string) => void;
  disabled?: boolean;
  testId?: string;
}

export function MsComboBox<T>({
  id,
  selected,
  onChange,
  getKey,
  getLabel,
  getSublabel,
  getColor,
  icon,
  placeholder = '',
  items,
  suggestions,
  onQueryChange,
  onFreeEntry,
  disabled = false,
  testId,
}: Props<T>) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  useDismissOnOutsideClick(containerRef, open, () => setOpen(false));
  const [focused, setFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const selectedKeys = new Set(selected.map(getKey));

  // Static mode: filter items internally. Async mode: use suggestions as-is.
  const pool = items !== undefined
    ? (query ? items.filter(i => getLabel(i).toLowerCase().includes(query.toLowerCase())) : items)
    : (suggestions ?? []);
  const filtered = pool.filter(i => !selectedKeys.has(getKey(i)));

  const add = (item: T) => {
    onChange([...selected, item]);
    setQuery('');
    setOpen(false);
    if (onQueryChange) {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      onQueryChange('');
    }
    inputRef.current?.focus();
  };

  const remove = (key: string) => onChange(selected.filter(i => getKey(i) !== key));

  useEffect(() => () => { if (debounceRef.current) clearTimeout(debounceRef.current); }, []);

  const handleQueryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const q = e.target.value;
    setQuery(q);
    setOpen(q.length > 0);
    if (onQueryChange) {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => onQueryChange(q), 300);
    }
  };

  const handleFocus = () => setFocused(true);
  const handleBlur = () => setFocused(false);

  return (
    <div
      ref={containerRef}
      className={`ms-ctn form-control${focused ? ' ms-ctn-focus' : ''}${disabled ? ' disabled' : ''}`}
      id={id}
      data-testid={testId}
      onClick={() => !disabled && inputRef.current?.focus()}
      style={{ height: 'auto', minHeight: 34, ...(disabled ? { opacity: 0.65, pointerEvents: 'none' } : {}) }}
    >
      <div className="ms-sel-ctn" style={{ overflow: 'hidden' }}>
        {selected.map(item => {
          const key = getKey(item);
          const sub = getSublabel?.(item);
          const color = getColor ? getColor(item) : '#428bca';
          return (
            <span key={key} className="ms-sel-item" data-testid="combo-chip">
              {icon && <i className={`fas fa-fw ${icon}`} style={{ color }} aria-hidden="true" />}{' '}
              {getLabel(item)}
              {sub && <span className="text-muted"> ({sub})</span>}
              <span
                className="ms-close-btn"
                role="button"
                data-testid="combo-remove-btn"
                aria-label={`Remove ${getLabel(item)}`}
                onClick={e => { e.stopPropagation(); remove(key); }}
              />
            </span>
          );
        })}
        <input
          ref={inputRef}
          type="text"
          value={query}
          data-testid="combo-input"
          placeholder={selected.length === 0 ? placeholder : ''}
          style={selected.length > 0 ? { width: 60 } : undefined}
          onChange={handleQueryChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onKeyDown={e => {
            if (e.key === 'Enter' && query.trim() && onFreeEntry) {
              e.preventDefault();
              onFreeEntry(query.trim());
              setQuery('');
              setOpen(false);
              if (debounceRef.current) clearTimeout(debounceRef.current);
              if (onQueryChange) onQueryChange('');
            }
          }}
        />
      </div>
      {open && filtered.length > 0 && (
        <div className="ms-res-ctn dropdown-menu" style={{ display: 'block', maxHeight: 200 }}>
          {filtered.map((item, i) => {
            const sub = getSublabel?.(item);
            return (
              <div
                key={getKey(item)}
                className={`ms-res-item${i % 2 === 1 ? ' ms-res-odd' : ''}`}
                data-testid="combo-option-item"
                onMouseDown={e => { e.preventDefault(); add(item); }}
              >
                {icon && <i className={`fas fa-fw ${icon}`} style={getColor ? { color: getColor(item) } : undefined} aria-hidden="true" />}{' '}
                {getLabel(item)}
                {sub && <span className="text-muted"> ({sub})</span>}
              </div>
            );
          })}
        </div>
      )}
      <div className="ms-trigger" data-testid="combo-toggle-btn" onMouseDown={e => { e.preventDefault(); setOpen(o => !o); }}>
        <div className="ms-trigger-ico" />
      </div>
    </div>
  );
}
